import { airspacesForRoute, airportByCode, regionById } from "../data/worldQueries";
import { clamp, deterministicHash, signed } from "../utils";
import type { Aircraft, Airport, EventEffect, GlobalEvent, License } from "../types";


export interface DailyEventDisruptionState {
  day: number;
  reputation: number;
  licenses: License[];
  fleet: Aircraft[];
}

export function normalizeEventEffects(value: unknown): EventEffect[] {
  if (!Array.isArray(value)) {
    return [{ type: "demand_multiplier", multiplier: 1.25 }];
  }
  const effects = value.map(normalizeEventEffect).filter((effect): effect is EventEffect => Boolean(effect));
  if (effects.length > 0) {
    return effects;
  }
  return [{ type: "demand_multiplier", multiplier: 1.25 }];
}

function normalizeEventEffect(value: unknown): EventEffect | null {
  if (!value || typeof value !== "object" || !("type" in value)) {
    return null;
  }
  const type = field(value, "type");
  const scalar = field(value, "value");
  if (type === "demand_multiplier") {
    return { type, multiplier: finiteNumber(field(value, "multiplier"), finiteNumber(scalar, 1.25)) };
  }
  if (type === "fuel_price_delta") {
    return { type, usdPerTonDelta: finiteNumber(field(value, "usdPerTonDelta"), finiteNumber(scalar, 150)) };
  }
  if (type === "airspace_status") {
    const status = field(value, "status");
    let normalizedStatus: "open" | "restricted" | "closed" = "closed";
    if (status === "open" || status === "restricted" || status === "closed") {
      normalizedStatus = status;
    }
    return {
      type,
      status: normalizedStatus,
      delayMinutes: optionalFiniteNumber(field(value, "delayMinutes"))
    };
  }
  if (type === "airport_capacity_multiplier") {
    return { type, multiplier: finiteNumber(field(value, "multiplier"), finiteNumber(scalar, 0.5)) };
  }
  if (type === "license_pressure") {
    return { type, progressDelta: finiteNumber(field(value, "progressDelta"), finiteNumber(scalar, -8)) };
  }
  if (type === "reputation_delta") {
    return { type, points: finiteNumber(field(value, "points"), finiteNumber(scalar, -2)) };
  }
  if (type === "maintenance_disruption") {
    return {
      type,
      defectRate: finiteNumber(field(value, "defectRate"), finiteNumber(scalar, 0.12)),
      downtimeDays: maintenanceDisruptionDowntimeDays(value)
    };
  }
  return null;
}

function maintenanceDisruptionDowntimeDays(value: unknown): number {
  const rawDowntimeDays = finiteNumber(field(value, "downtimeDays"), 1);
  const roundedDowntimeDays = Math.round(rawDowntimeDays);
  return Math.max(1, roundedDowntimeDays);
}

function field(body: unknown, key: string): unknown {
  if (body && typeof body === "object" && key in body) {
    return (body as Record<string, unknown>)[key];
  }
  return undefined;
}

function finiteNumber(value: unknown, fallback: number): number {
  let numeric = NaN;
  if (typeof value === "number") {
    numeric = value;
  } else if (typeof value === "string" && value.trim()) {
    numeric = Number(value);
  }
  if (Number.isFinite(numeric)) {
    return numeric;
  }
  return fallback;
}

function optionalFiniteNumber(value: unknown): number | undefined {
  const numeric = finiteNumber(value, NaN);
  if (Number.isFinite(numeric)) {
    return numeric;
  }
  return undefined;
}

export function airportCapacityMultiplier(airport: Airport, events: GlobalEvent[]): number {
  return events.reduce((multiplier, event) => {
    if (!eventMatchesAirport(event, airport)) {
      return multiplier;
    }
    return (
      multiplier *
      event.effects.reduce((inner, effect) => {
        if (effect.type === "airport_capacity_multiplier") {
          return inner * effect.multiplier;
        }
        return inner;
      }, 1)
    );
  }, 1);
}

export function totalAirspaceDelayMinutes(origin: Airport, destination: Airport, events: GlobalEvent[]): number {
  return airspacesForRoute(origin, destination).reduce((sum, airspace) => {
    const eventDelay = events.reduce((delay, event) => {
      if (!eventMatchesAirspace(event, airspace.id, origin, destination)) {
        return delay;
      }
      return (
        delay +
        event.effects.reduce((inner, effect) => {
          if (effect.type === "airspace_status" && effect.status === "restricted") {
            return inner + (effect.delayMinutes ?? 45);
          }
          return inner;
        }, 0)
      );
    }, 0);
    return sum + eventDelay;
  }, 0);
}

export function airspaceStatus(airspaceId: string, origin: Airport, destination: Airport, events: GlobalEvent[]): "open" | "restricted" | "closed" {
  let status: "open" | "restricted" | "closed" = "open";
  for (const event of events) {
    if (!eventMatchesAirspace(event, airspaceId, origin, destination)) {
      continue;
    }
    for (const effect of event.effects) {
      if (effect.type === "airspace_status") {
        status = effect.status;
      }
    }
  }
  return status;
}

export function applyDailyEventDisruptions(state: DailyEventDisruptionState, events: GlobalEvent[], alerts: string[]) {
  for (const event of events) {
    for (const effect of event.effects) {
      if (effect.type === "reputation_delta") {
        state.reputation = clamp(state.reputation + effect.points, 0, 100);
        alerts.push(`${event.title}: 声誉调整 ${signed(effect.points)}。`);
      }
      if (effect.type === "license_pressure") {
        for (const license of state.licenses) {
          const region = regionById(license.regionId);
          if (!region || (event.scopeType === "region" && event.scopeValue !== region.id)) {
            continue;
          }
          license.progress = clamp(license.progress + effect.progressDelta, 0, 100);
        }
      }
      if (effect.type === "maintenance_disruption") {
        for (const aircraft of state.fleet) {
          const airport = airportByCode(aircraft.location);
          if (!airport || !eventMatchesAirport(event, airport)) {
            continue;
          }
          const hash = deterministicHash(`${event.id}-${aircraft.id}-${state.day}`);
          if ((hash % 100) / 100 < effect.defectRate) {
            aircraft.status = "maintenance";
            aircraft.availableFromDay = state.day + effect.downtimeDays;
            alerts.push(`${aircraft.registration}: ${event.title} 触发临修停场 ${effect.downtimeDays} 天。`);
          }
        }
      }
    }
  }
}

export function eventMatchesAirport(event: GlobalEvent, airport: Airport): boolean {
  if (event.status !== "active") {
    return false;
  }
  if (event.scopeType === "global") {
    return true;
  }
  if (event.scopeType === "airport") {
    return event.scopeValue === airport.code;
  }
  if (event.scopeType === "region") {
    return event.scopeValue === airport.regionId;
  }
  if (event.scopeType === "fuel_zone") {
    return event.scopeValue === airport.fuelZoneId;
  }
  return false;
}

export function eventMatchesAirspace(event: GlobalEvent, airspaceId: string, origin: Airport, destination: Airport): boolean {
  if (event.status !== "active") {
    return false;
  }
  if (event.scopeType === "global") {
    return true;
  }
  if (event.scopeType === "airspace") {
    return event.scopeValue === airspaceId;
  }
  if (event.scopeType === "region") {
    return event.scopeValue === origin.regionId || event.scopeValue === destination.regionId;
  }
  if (event.scopeType === "airport") {
    return event.scopeValue === origin.code || event.scopeValue === destination.code;
  }
  return false;
}
